import ensureError from "@-/com.npmjs.ensure-error";
import onetime from "@-/com.npmjs.onetime";
import type { ComponentType } from "react";
import { useEffect, useState } from "react";

type ComponentLoader<Props> = () => Promise<ComponentType<Props> | null>;
type AsyncComponentState<Props> = { loadComponent: ComponentLoader<Props> } & (
  | { Component: ComponentType<Props>; error: null }
  | { Component: null; error: Error | null }
);

// A code loader identifies an immutable module, not a mounted view. Reuse successful loads
// synchronously so a warm surface does not mount a throwaway fallback before its real renderer.
// Weak keys let temporary loaders disappear; failures remain retryable on a fresh mount.
const getResolvedComponents = onetime(() => new WeakMap<object, unknown>());

function resolvedState<Props>(loadComponent: ComponentLoader<Props>) {
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- Each entry is written with this exact loader's Props; WeakMap cannot express the key/value type relationship.
  return getResolvedComponents().get(loadComponent) as AsyncComponentState<Props> | undefined;
}

function initialState<Props>(loadComponent: ComponentLoader<Props>): AsyncComponentState<Props> {
  return resolvedState(loadComponent) ?? { Component: null, error: null, loadComponent };
}

/**
 * Loads a code-split component (a chart, a heavy editor, …) without using `React.lazy`. A lazy
 * component throws its import promise during render, which means an otherwise fully-prepared page
 * can still enter Suspense merely because the chunk has not loaded yet. This hook keeps the code
 * split while making chunk loading an ordinary, non-suspending render state: `null` until the
 * loader resolves, then the component. Subsequent consumers render it on their first mount.
 * A rejected loader is rethrown from render so the nearest
 * error boundary handles it.
 *
 * `loadComponent` MUST be referentially stable (e.g. a module-level `pMemoize`d function) — it is
 * an effect dependency, and a fresh closure per render would re-run the load every commit.
 *
 * A loader may also resolve to `null` to mean "this component is deliberately unavailable in this
 * build" (e.g. the Skia stats chart, which a CI build opts out of because Skia's Graphite backend
 * aborts the process on a GPU-less emulator). That is indistinguishable from "still loading" at the
 * call site, which is exactly right: consumers already render their placeholder for `null`.
 */
export function useAsyncComponent<Props>(
  loadComponent: ComponentLoader<Props>,
): ComponentType<Props> | null {
  const [state, setState] = useState(() => initialState(loadComponent));

  useEffect(() => {
    const resolved = resolvedState(loadComponent);
    if (resolved !== undefined) {
      setState(resolved);
      return;
    }
    let isCurrent = true;
    void loadComponent().then(
      (Component) => {
        const loaded: AsyncComponentState<Props> = { Component, error: null, loadComponent };
        // Finishing an import is useful even if the surface that requested it was retired.
        getResolvedComponents().set(loadComponent, loaded);
        if (isCurrent) {
          setState(loaded);
        }
      },
      (error: unknown) => {
        if (isCurrent) {
          setState({ Component: null, error: ensureError(error), loadComponent });
        }
      },
    );
    return () => {
      isCurrent = false;
    };
  }, [loadComponent]);

  const current = state.loadComponent === loadComponent ? state : initialState(loadComponent);
  if (current.error !== null) {
    throw current.error;
  }
  return current.Component;
}
